/**
 * 개발자 후원 — 건전지 (SC-023 · 수익모델 §4).
 *
 * 이 앱은 구독도 유료 티어도 두지 않는다. 기능은 전부 무료이고, 후원의 보상은
 * **표식뿐**이다 — 건전지 개수. 광고는 후원 뒤에도 그대로 켜져 있다.
 * "기능이든 광고든 인질로 잡는 순간 후원이 아니게 된다"(수익모델 §4.2).
 *
 * 누적 개수의 출처는 **서버(계정 기준)** 다 — 소모성 IAP 는 복원이 안 돼서 기기에 두면
 * 앱을 지우는 순간 사라진다(OI-10). 화면은 `hooks/useDonationQueries` 를 쓴다.
 * 이 파일에는 **화면을 그리는 순수 계산만** 남는다(단계·색·픽토그램 레벨).
 */

/**
 * 소모성 IAP 단일가 — **폴백 전용**. 수익모델 §4.2, 커피 한 잔 값의 심리선을 넘지 않는다.
 *
 * ⚠ **화면에 그대로 쓰지 말 것.** 표시 가격은 스토어가 주는 로케일 가격
 * (`localizedPrice`)이어야 한다. 이 값을 그대로 그리면 en/ja 스토어에서 "3,900원" 이라
 * 써 놓고 $2.99 를 청구하는 꼴이 되고, 부정확한 메타데이터로 심사에서 걸린다.
 * peeka 는 ko/en/ja 3개 스토어에 나가므로 남 얘기가 아니다.
 *
 * 스토어 상품 조회 전(또는 조회 실패)에만 이 값을 쓴다.
 */
export const DONATION_PRICE_KRW = 3900;

/**
 * 표식 단계 (수익모델 §4.3).
 *
 * 실질 보상이 없으므로 표식이 후원의 유일한 동기다. 카드를 모으는 사람은
 * 건전지도 모은다 — 개수가 쌓이는 것 자체가 동력이 된다.
 *
 * ⚠ **본인 전용이다.** 프로필 공개·공유 이미지 노출을 하지 않고, '건전지 0개'
 *   같은 부재 표시도, 순위·랭킹도 만들지 않는다 (2026-08-25 확정 · BRAND §3.10).
 *   후원의 KPI 는 금액이 아니라 후원자 수·반복률이다.
 */
export type BatteryTier = "none" | "one" | "few" | "many" | "lots";

export function batteryTier(count: number): BatteryTier {
  if (count >= 10) return "lots";
  if (count >= 5) return "many";
  if (count >= 3) return "few";
  if (count >= 1) return "one";
  return "none";
}

/**
 * 픽토그램 단계 (2026-08-27).
 *
 * 그림이 **열 장**인데 문서의 tier 는 다섯이다. 둘은 다른 축이다 —
 * tier 는 작은 아이콘의 **색**을 정하고, 이 함수는 큰 자리에 걸리는
 * **그림**을 정한다. 그림은 개수를 그대로 나른다.
 *
 * ⚠ **01(빈 배터리)은 안 쓴다.** 후원의 유일한 보상이 표식인데 첫 후원에
 *   빈 그림을 보여 줄 수는 없다. 그렇다고 '아직 안 넣음' 에 걸 수도 없다 —
 *   '건전지 0개' 같은 부재 표시는 만들지 않기로 했다 (수익모델 §4.3).
 *   0 개는 **아무것도 안 그린다**. 그래서 null 이다.
 *
 *   1개 → 02 (불이 들어온다)      5개 → 06 (반짝임이 는다)
 *   2개 → 03                      6개 → 07 (궤도가 돈다)
 *   3개 → 04 (가득 찬다)          7개 → 08 (뿜는다)
 *   4개 → 05 (반짝인다)           8개 → 09 (터진다)
 *                                 9개~ → 10 (더 크게 터진다)
 *
 * 문서의 tier 경계가 우연히 결이 맞는다 — few(3개)가 '가득', many(5개)가
 * '반짝임이 는다' 에 떨어진다.
 */
export function batteryLevel(count: number): number | null {
  if (count < 1) return null;
  return Math.min(count + 1, 10);
}

/** 단계별 표식 색. none 은 아무것도 그리지 않으므로 색이 없다. */
export const BATTERY_TIER_COLOR: Record<Exclude<BatteryTier, "none">, string> = {
  one: "var(--text-secondary)",
  few: "var(--color-text-accent)",
  many: "var(--color-accent)",
  lots: "var(--color-accent)",
};
